<script setup lang="ts">
/**
 * Datumveld (dd/mm/jjjj) op basis van vue-datepicker. Het model is een
 * `YYYY-MM-DD`-string, net als in de database. Vervangt `<v-input type="date">`.
 */
import { usePage } from '@inertiajs/vue3'
import VueDatePicker from '@vuepic/vue-datepicker'
import { computed, ref, useSlots } from 'vue'
import { formatLocalDate } from '@/HelperFunctions'
import type { PageProps } from '@/Models/Inertia'
import VFormField from './VFormField.vue'
import type { FormFieldBaseProps } from './types'
import { controlClasses, useFieldErrors, useFormFieldBindings, useSplitAttrs } from './useFormField'

defineOptions({ inheritAttrs: false })

const modelValue = defineModel<string | null>()

const props = withDefaults(
    defineProps<
        FormFieldBaseProps & {
            /**
             * Toont de datum als gewone, niet-bewerkbare input. Klikken valt door naar de
             * parent (bv. Vervaldatum opent de instellingenmodal via `@click`).
             */
            readonly?: boolean
            /** Vroegst kiesbare datum, `YYYY-MM-DD` of Date. VInput typeerde dit als number. */
            min?: string | Date | null
            max?: string | Date | null
        }
    >(),
    {
        readonly: false,
        min: null,
        max: null,
        showErrors: true,
    }
)

const page = usePage<PageProps>()
const slots = useSlots()
const { rootAttrs, controlAttrs } = useSplitAttrs()
const errors = useFieldErrors(props)
const fieldBindings = useFormFieldBindings(props, errors)

const fieldRef = ref<InstanceType<typeof VFormField> | null>(null)

/** De datepicker rendert zijn eigen input; die zoeken we op in de veldwrapper. */
function focus(): void {
    ;(fieldRef.value?.$el as HTMLElement | undefined)?.querySelector('input')?.focus()
}

defineExpose({ focus })

const classes = computed<string>(() =>
    controlClasses({
        inputClasses: props.inputClasses,
        invalid: props.invalid,
        hasPrefix: !!slots.prefix,
        hasSuffix: !!slots.suffix,
    })
)

/**
 * Converteert tussen YYYY-MM-DD (model) en Date (vue-datepicker). Lokale
 * datum-methodes, zodat er geen dag verschuift door UTC.
 */
const datePickerValue = computed<Date | null>({
    get() {
        if (!modelValue.value || typeof modelValue.value !== 'string') {
            return null
        }
        const [year, month, day] = modelValue.value.split('-').map(Number)
        if (!year || !month || !day) {
            return null
        }
        return new Date(year, month - 1, day)
    },
    set(date: Date | null) {
        modelValue.value = date ? formatLocalDate(date) : null
    },
})

const readonlyText = computed<string>(() => {
    const date = datePickerValue.value
    if (!date) {
        return ''
    }

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')

    return `${day}/${month}/${date.getFullYear()}`
})
</script>

<template>
    <VFormField ref="fieldRef" v-bind="{ ...fieldBindings, ...rootAttrs }">
        <template v-if="$slots.prefix" #prefix><slot name="prefix"></slot></template>

        <input
            v-if="readonly"
            v-bind="controlAttrs"
            :value="readonlyText"
            :id="id ?? name ?? undefined"
            :name="name ?? undefined"
            type="text"
            readonly
            :class="classes"
            :disabled="disabled"
        />

        <vue-date-picker
            v-else
            v-bind="controlAttrs"
            v-model="datePickerValue"
            :locale="page.props.locale"
            :enable-time-picker="false"
            :clearable="false"
            format="dd/MM/yyyy"
            :disabled="disabled"
            :readonly="false"
            :min-date="min ?? undefined"
            :max-date="max ?? undefined"
            :uid="id ?? name ?? undefined"
            text-input
            auto-apply
            hide-input-icon
        />

        <template v-if="$slots.suffix" #suffix><slot name="suffix"></slot></template>
    </VFormField>
</template>
